import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Brain, FileText, Fingerprint, Lock, ShieldIcon, TrendingUp } from 'lucide-react';
import Footer from './ZeronineFooter';
import Navigation from './ZeronineHeader';
import { getSettingForWeb } from '../../redux/actions/package';

const PrivacyPolicy = () => {
   const dispatch = useDispatch();
   const WebSetting = useSelector((state) => state.Package?.webSetting || []);

   console.log('WebSetting',WebSetting);
   
   useEffect(() => {
      dispatch(getSettingForWeb());
   }, [dispatch]);

   // Get privacy policy content from WebSetting
   const privacyPolicyContent = WebSetting[0]?.policy || '';

   return (
      <>
         <Navigation />
         <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 pt-32 pb-20 px-4">
            <div className="max-w-4xl mx-auto">
               <div className="text-center mb-12">
                  <div className="inline-block px-4 py-2 bg-green-100 rounded-full mb-6">
                     <span className="text-green-600 font-bold">🔒 PRIVACY</span>
                  </div>
                  <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">Privacy Policy</h1>
                  <p className="text-xl text-gray-600">How we protect and use your data</p>
               </div>

               <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-gray-200">
                  {privacyPolicyContent ? (
                     <div
                        className="prose prose-lg max-w-none"
                        dangerouslySetInnerHTML={{ __html: privacyPolicyContent }}
                     />
                  ) : (
                     <div className="prose prose-lg max-w-none">
                        <div className="mb-8 p-6 bg-green-50 rounded-2xl border border-green-200">
                           <p className="text-green-800 font-semibold">
                              Your privacy is critically important to us. We built ZeroNine AI with privacy and security
                              at its core.
                           </p>
                        </div>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4 flex items-center gap-3">
                              <ShieldIcon className="w-8 h-8 text-green-600" />
                              Information We Collect
                           </h2>
                           <div className="grid md:grid-cols-2 gap-6">
                              <div className="p-4 bg-blue-50 rounded-xl">
                                 <h3 className="font-bold text-blue-800 mb-2">Personal Information</h3>
                                 <ul className="text-sm text-blue-700 space-y-1">
                                    <li>• Business name and details</li>
                                    <li>• Contact information</li>
                                    <li>• GSTIN and tax details</li>
                                    <li>• Bank account information</li>
                                 </ul>
                              </div>
                              <div className="p-4 bg-purple-50 rounded-xl">
                                 <h3 className="font-bold text-purple-800 mb-2">Financial Data</h3>
                                 <ul className="text-sm text-purple-700 space-y-1">
                                    <li>• Invoice and transaction data</li>
                                    <li>• Expense records</li>
                                    <li>• Inventory information</li>
                                    <li>• Financial reports</li>
                                 </ul>
                              </div>
                           </div>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">How We Use Your Information</h2>
                           <div className="space-y-4">
                              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                 <Brain className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                                 <div>
                                    <h4 className="font-bold text-gray-900">AI Processing</h4>
                                    <p className="text-gray-700">
                                       To power our AI features including automated categorization, predictions, and
                                       insights
                                    </p>
                                 </div>
                              </div>
                              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                 <TrendingUp className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                                 <div>
                                    <h4 className="font-bold text-gray-900">Service Improvement</h4>
                                    <p className="text-gray-700">
                                       To enhance and personalize your experience with our accounting features
                                    </p>
                                 </div>
                              </div>
                              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                 <FileText className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                                 <div>
                                    <h4 className="font-bold text-gray-900">GST Compliance</h4>
                                    <p className="text-gray-700">
                                       To generate and file GST returns as per government requirements
                                    </p>
                                 </div>
                              </div>
                           </div>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">Data Security</h2>
                           <div className="grid md:grid-cols-2 gap-6">
                              <div className="p-6 bg-red-50 rounded-xl border border-red-200">
                                 <Lock className="w-8 h-8 text-red-600 mb-3" />
                                 <h3 className="font-bold text-red-800 mb-2">Encryption</h3>
                                 <p className="text-red-700 text-sm">
                                    All data encrypted with 256-bit AES encryption in transit and at rest
                                 </p>
                              </div>
                              <div className="p-6 bg-indigo-50 rounded-xl border border-indigo-200">
                                 <Fingerprint className="w-8 h-8 text-indigo-600 mb-3" />
                                 <h3 className="font-bold text-indigo-800 mb-2">Access Control</h3>
                                 <p className="text-indigo-700 text-sm">
                                    Role-based access control and multi-factor authentication
                                 </p>
                              </div>
                           </div>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">Your Rights</h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              You have the right to access, correct, or delete your personal information. You can also
                              object to processing of your personal data, ask us to restrict processing, or request
                              portability of your personal data.
                           </p>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">Contact Us</h2>
                           <p className="text-gray-700 leading-relaxed">
                              If you have any questions about this Privacy Policy, please contact our Data Protection
                              Officer:
                           </p>
                           <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                              <p className="text-gray-700 font-semibold">Email: help@zeronine.ai</p>
                              <p className="text-gray-700 font-semibold">Phone: +91 72839-90909</p>
                           </div>
                        </section>
                     </div>
                  )}
               </div>
            </div>
         </div>
         <Footer />
      </>
   );
};

export default PrivacyPolicy;
